/**
 * Accounts Service
 * Handles all database operations for the accounting app
 * Double-entry bookkeeping system
 */
import { supabase as supabaseClient } from '@/lib/supabase-client';
// Use the properly typed Supabase client
const supabase = supabaseClient;
// ============================================
// HELPERS
// ============================================
/**
 * Transform snake_case database response to camelCase TypeScript types
 */
function transformTransaction(data) {
    if (!data)
        return data;
    return {
        id: data.id,
        organizationId: data.organization_id,
        transactionDate: data.transaction_date,
        debitAccountId: data.debit_account_id,
        creditAccountId: data.credit_account_id,
        amount: data.amount,
        description: data.description,
        categoryId: data.category_id,
        notes: data.notes,
        link: data.link,
        isReconciled: data.is_reconciled,
        reconciledAt: data.reconciled_at,
        reconciledBy: data.reconciled_by,
        createdBy: data.created_by,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        debitAccount: data.debit_account ? transformAccount(data.debit_account) : undefined,
        creditAccount: data.credit_account ? transformAccount(data.credit_account) : undefined,
        category: data.category ? transformCategory(data.category) : undefined,
    };
}
/**
 * Transform account from snake_case to camelCase
 */
function transformAccount(data) {
    if (!data)
        return data;
    return {
        id: data.id,
        organizationId: data.organization_id,
        entityId: data.entity_id,
        name: data.name,
        type: data.type,
        balance: data.balance,
        currency: data.currency,
        isActive: data.is_active,
        lastReconciled: data.last_reconciled,
        accountNumber: data.account_number,
        createdBy: data.created_by,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
    };
}
/**
 * Transform category from snake_case to camelCase
 */
function transformCategory(data) {
    if (!data)
        return data;
    return {
        id: data.id,
        organizationId: data.organization_id,
        name: data.name,
        emoji: data.emoji,
        parentId: data.parent_id,
        type: data.type,
        budgetAmount: data.budget_amount,
        color: data.color,
        createdBy: data.created_by,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
    };
}
/**
 * Transform financial details from snake_case to camelCase
 */
function transformFinancialDetails(data) {
    if (!data)
        return data;
    return {
        id: data.id,
        organizationId: data.organization_id,
        accountId: data.account_id,
        financialStatus: data.financial_status,
        nextPayableDate: data.next_payable_date,
        nextPaymentUSD: data.next_payment_usd,
        nextPaymentINR: data.next_payment_inr,
        payablePeriodStart: data.payable_period_start,
        payablePeriodEnd: data.payable_period_end,
        totalBalanceUSD: data.total_balance_usd,
        totalBalanceINR: data.total_balance_inr,
        usdUnits: data.usd_units,
        inrUnits: data.inr_units,
        createdBy: data.created_by,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
    };
}
/**
 * Transform billing schedule from snake_case to camelCase
 */
function transformBillingSchedule(data) {
    if (!data)
        return data;
    return {
        id: data.id,
        organizationId: data.organization_id,
        accountId: data.account_id,
        billingInterval: data.billing_interval,
        priority: data.priority,
        urgency: data.urgency,
        mondayDocReference: data.monday_doc_reference,
        createdBy: data.created_by,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
    };
}
/**
 * Transform categorization from snake_case to camelCase
 */
function transformCategorization(data) {
    if (!data)
        return data;
    return {
        id: data.id,
        organizationId: data.organization_id,
        accountId: data.account_id,
        category: data.category,
        subCategory: data.sub_category,
        accountOwnerGroup: data.account_owner_group,
        accountTypeCategory: data.account_type_category,
        createdBy: data.created_by,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
    };
}
/**
 * Transform time tracking entry from snake_case to camelCase
 */
function transformTimeTrackingEntry(data) {
    if (!data)
        return data;
    return {
        id: data.id,
        organizationId: data.organization_id,
        accountId: data.account_id,
        projectName: data.project_name,
        startedBy: data.started_by,
        endedBy: data.ended_by,
        startDate: data.start_date,
        endDate: data.end_date,
        startTime: data.start_time,
        endTime: data.end_time,
        duration: data.duration,
        notes: data.notes,
        createdBy: data.created_by,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
    };
}
/**
 * Transform account update from snake_case to camelCase
 */
function transformAccountUpdate(data) {
    if (!data)
        return data;
    return {
        id: data.id,
        organizationId: data.organization_id,
        accountId: data.account_id,
        previousValues: data.previous_values,
        newValues: data.new_values,
        changedBy: data.changed_by,
        changeReason: data.change_reason,
        createdAt: data.created_at,
    };
}
// ============================================
// ENTITIES
// ============================================
export async function fetchEntities(organizationId) {
    try {
        const { data, error } = await supabase
            .from('entities')
            .select('*')
            .eq('organization_id', organizationId)
            .order('created_at', { ascending: false });
        if (error)
            throw error;
        return data || [];
    }
    catch (error) {
        console.error('Error fetching entities:', error);
        throw error;
    }
}
export async function createEntity(organizationId, userId, data) {
    try {
        const { data: entity, error } = await supabase
            .from('entities')
            .insert([
            {
                organization_id: organizationId,
                name: data.name,
                type: data.type,
                created_by: userId,
            },
        ])
            .select()
            .single();
        if (error)
            throw error;
        return entity;
    }
    catch (error) {
        console.error('Error creating entity:', error);
        throw error;
    }
}
// ============================================
// ACCOUNTS
// ============================================
export async function fetchAccounts(organizationId) {
    try {
        const { data, error } = await supabase
            .from('accounts')
            .select('*')
            .eq('organization_id', organizationId)
            .eq('is_active', true)
            .order('created_at', { ascending: false });
        if (error)
            throw error;
        return (data || []).map(transformAccount);
    }
    catch (error) {
        console.error('Error fetching accounts:', error);
        throw error;
    }
}
export async function fetchAccountById(accountId) {
    try {
        const { data, error } = await supabase
            .from('accounts')
            .select('*')
            .eq('id', accountId)
            .single();
        if (error)
            throw error;
        return transformAccount(data);
    }
    catch (error) {
        console.error('Error fetching account:', error);
        throw error;
    }
}
export async function createAccount(organizationId, userId, input) {
    try {
        const accountData = {
            organization_id: organizationId,
            name: input.name,
            type: input.type,
            currency: input.currency,
            balance: input.balance || 0,
            is_active: true,
            created_by: userId,
        };
        // Only add optional fields if they're provided
        if (input.entityId) {
            accountData.entity_id = input.entityId;
        }
        if (input.accountNumber) {
            accountData.account_number = input.accountNumber;
        }
        const { data, error } = await supabase
            .from('accounts')
            .insert([accountData])
            .select()
            .single();
        if (error)
            throw error;
        return transformAccount(data);
    }
    catch (error) {
        console.error('Error creating account:', error);
        throw error;
    }
}
export async function updateAccount(accountId, updates) {
    try {
        const { data, error } = await supabase
            .from('accounts')
            .update(updates)
            .eq('id', accountId)
            .select()
            .single();
        if (error)
            throw error;
        return transformAccount(data);
    }
    catch (error) {
        console.error('Error updating account:', error);
        throw error;
    }
}
// ============================================
// CATEGORIES
// ============================================
export async function fetchCategories(organizationId) {
    try {
        const { data, error } = await supabase
            .from('account_categories')
            .select('*')
            .eq('organization_id', organizationId)
            .order('name', { ascending: true });
        if (error)
            throw error;
        return (data || []).map(transformCategory);
    }
    catch (error) {
        console.error('Error fetching categories:', error);
        throw error;
    }
}
export async function createCategory(organizationId, userId, input) {
    try {
        const { data, error } = await supabase
            .from('account_categories')
            .insert([
            {
                organization_id: organizationId,
                name: input.name,
                emoji: input.emoji,
                type: input.type,
                parent_id: input.parentId,
                budget_amount: input.budgetAmount,
                color: input.color,
                created_by: userId,
            },
        ])
            .select()
            .single();
        if (error)
            throw error;
        return transformCategory(data);
    }
    catch (error) {
        console.error('Error creating category:', error);
        throw error;
    }
}
export async function seedDefaultCategories(organizationId, userId) {
    try {
        const defaultCategories = [
            { name: 'Rentals', emoji: '🏘', type: 'expense' },
            { name: 'Household', emoji: '🧹', type: 'expense' },
            { name: 'Cash', emoji: '💵', type: 'expense' },
            { name: 'Taxes & Student Loans', emoji: '🧑‍⚖️', type: 'expense' },
            { name: 'Subscriptions', emoji: '📱', type: 'expense' },
            { name: 'Healthcare Savings', emoji: '⛑', type: 'expense' },
            { name: 'Pocket Money', emoji: '🤑', type: 'expense' },
            { name: 'Entertainment', emoji: '🍿', type: 'expense' },
            { name: 'Business Expenses A', emoji: '📊', type: 'expense' },
            { name: 'Business Expenses B', emoji: '💼', type: 'expense' },
            { name: 'Urgent Debts', emoji: '💳', type: 'expense' },
            { name: 'Transfers', emoji: '🤝🏾', type: 'transfer' },
            { name: 'Capital Investment', emoji: '🪴', type: 'investment' },
        ];
        for (const cat of defaultCategories) {
            await createCategory(organizationId, userId, cat);
        }
    }
    catch (error) {
        console.error('Error seeding default categories:', error);
        // Don't throw - this is optional
    }
}
// ============================================
// TRANSACTIONS
// ============================================
export async function fetchTransactions(organizationId, limit = 50, offset = 0) {
    try {
        const { data, error, count } = await supabase
            .from('account_transactions')
            .select(`
        *,
        debit_account:accounts!debit_account_id(*),
        credit_account:accounts!credit_account_id(*),
        category:account_categories(*)
        `, { count: 'exact' })
            .eq('organization_id', organizationId)
            .order('transaction_date', { ascending: false })
            .range(offset, offset + limit - 1);
        if (error)
            throw error;
        return {
            data: (data || []).map(transformTransaction),
            count: data?.length || 0,
            total: count || 0,
        };
    }
    catch (error) {
        console.error('Error fetching transactions:', error);
        throw error;
    }
}
export async function fetchTransactionsByAccount(accountId, limit = 100) {
    try {
        const { data, error } = await supabase
            .from('account_transactions')
            .select(`
        *,
        debit_account:accounts!debit_account_id(*),
        credit_account:accounts!credit_account_id(*),
        category:account_categories(*)
        `)
            .or(`debit_account_id.eq.${accountId},credit_account_id.eq.${accountId}`)
            .order('transaction_date', { ascending: false })
            .limit(limit);
        if (error)
            throw error;
        return (data || []).map(transformTransaction);
    }
    catch (error) {
        console.error('Error fetching account transactions:', error);
        throw error;
    }
}
export async function createTransaction(organizationId, userId, input) {
    try {
        // Validate accounts are different
        if (input.debitAccountId === input.creditAccountId) {
            throw new Error('Debit and credit accounts must be different');
        }
        const { data, error } = await supabase
            .from('account_transactions')
            .insert([
            {
                organization_id: organizationId,
                transaction_date: input.transactionDate,
                debit_account_id: input.debitAccountId,
                credit_account_id: input.creditAccountId,
                amount: input.amount,
                description: input.description,
                category_id: input.categoryId || null,
                notes: input.notes || null,
                link: null,
                is_reconciled: false,
                created_by: userId,
            },
        ])
            .select(`
        *,
        debit_account:accounts!debit_account_id(*),
        credit_account:accounts!credit_account_id(*),
        category:account_categories(*)
        `)
            .single();
        if (error) {
            console.error('Supabase error details:', error);
            throw error;
        }
        return transformTransaction(data);
    }
    catch (error) {
        console.error('Error creating transaction:', error);
        throw error;
    }
}
export async function reconcileTransaction(transactionId, userId) {
    try {
        const { data, error } = await supabase
            .from('account_transactions')
            .update({
            is_reconciled: true,
            reconciled_at: new Date().toISOString(),
            reconciled_by: userId,
        })
            .eq('id', transactionId)
            .select()
            .single();
        if (error)
            throw error;
        return data;
    }
    catch (error) {
        console.error('Error reconciling transaction:', error);
        throw error;
    }
}
export async function deleteTransaction(transactionId) {
    try {
        const { error } = await supabase
            .from('account_transactions')
            .delete()
            .eq('id', transactionId);
        if (error)
            throw error;
    }
    catch (error) {
        console.error('Error deleting transaction:', error);
        throw error;
    }
}
// ============================================
// RECURRING TRANSACTIONS
// ============================================
export async function fetchRecurringTransactions(organizationId) {
    try {
        const { data, error } = await supabase
            .from('recurring_transactions')
            .select('*')
            .eq('organization_id', organizationId)
            .eq('is_active', true)
            .order('next_occurrence', { ascending: true });
        if (error)
            throw error;
        return data || [];
    }
    catch (error) {
        console.error('Error fetching recurring transactions:', error);
        throw error;
    }
}
export async function createRecurringTransaction(organizationId, userId, data) {
    try {
        const { data: transaction, error } = await supabase
            .from('recurring_transactions')
            .insert([
            {
                organization_id: organizationId,
                debit_account_id: data.debitAccountId,
                credit_account_id: data.creditAccountId,
                amount: data.amount,
                description: data.description,
                category_id: data.categoryId,
                frequency: data.frequency,
                start_date: data.startDate,
                end_date: data.endDate,
                next_occurrence: data.nextOccurrence,
                is_active: true,
                created_by: userId,
            },
        ])
            .select()
            .single();
        if (error)
            throw error;
        return transaction;
    }
    catch (error) {
        console.error('Error creating recurring transaction:', error);
        throw error;
    }
}
// ============================================
// INVESTMENTS
// ============================================
export async function fetchInvestments(organizationId) {
    try {
        const { data, error } = await supabase
            .from('investments')
            .select('*')
            .eq('organization_id', organizationId)
            .order('investment_date', { ascending: false });
        if (error)
            throw error;
        return data || [];
    }
    catch (error) {
        console.error('Error fetching investments:', error);
        throw error;
    }
}
// ============================================
// BALANCE SHEETS
// ============================================
export async function fetchLatestBalanceSheet(organizationId, entityId) {
    try {
        let query = supabase
            .from('balance_sheets')
            .select('*')
            .eq('organization_id', organizationId);
        if (entityId) {
            query = query.eq('entity_id', entityId);
        }
        const { data, error } = await query
            .order('snapshot_date', { ascending: false })
            .limit(1)
            .single();
        if (error && error.code !== 'PGRST116') {
            // PGRST116 = no rows returned
            throw error;
        }
        return data || null;
    }
    catch (error) {
        console.error('Error fetching balance sheet:', error);
        return null;
    }
}
// ============================================
// DASHBOARD SUMMARY
// ============================================
export async function fetchDashboardSummary(organizationId) {
    try {
        // Fetch all accounts
        const accounts = await fetchAccounts(organizationId);
        // Fetch recent transactions
        const { data: transactions, error: txError } = await supabase
            .from('account_transactions')
            .select(`
        *,
        debit_account:accounts!debit_account_id(*),
        credit_account:accounts!credit_account_id(*),
        category:account_categories(*)
        `)
            .eq('organization_id', organizationId)
            .order('transaction_date', { ascending: false })
            .limit(10);
        if (txError)
            throw txError;
        // Count unreconciled
        const { count: unreconciledCount } = await supabase
            .from('account_transactions')
            .select('*', { count: 'exact', head: true })
            .eq('organization_id', organizationId)
            .eq('is_reconciled', false);
        // Calculate totals
        const totalAssets = accounts
            .filter((a) => a.type === 'checking' ||
            a.type === 'savings' ||
            a.type === 'cash' ||
            a.type === 'investment')
            .reduce((sum, a) => sum + a.balance, 0);
        const totalLiabilities = accounts
            .filter((a) => a.type === 'credit')
            .reduce((sum, a) => sum + Math.abs(a.balance), 0);
        const netWorth = totalAssets - totalLiabilities;
        // Group accounts by type
        const accountsByType = {};
        accounts.forEach((account) => {
            accountsByType[account.type] = (accountsByType[account.type] || 0) + 1;
        });
        return {
            totalAssets,
            totalLiabilities,
            netWorth,
            accountCount: accounts.length,
            unreconciledTransactionCount: unreconciledCount || 0,
            recentTransactions: (transactions || []).map(transformTransaction),
            accountsByType,
        };
    }
    catch (error) {
        console.error('Error fetching dashboard summary:', error);
        throw error;
    }
}
// ============================================
// ACCOUNT FINANCIAL DETAILS
// ============================================
export async function fetchAccountFinancialDetails(accountId) {
    try {
        const { data, error } = await supabase
            .from('account_financial_details')
            .select('*')
            .eq('account_id', accountId)
            .single();
        if (error && error.code !== 'PGRST116') {
            // PGRST116 = no rows returned
            throw error;
        }
        return data ? transformFinancialDetails(data) : null;
    }
    catch (error) {
        console.error('Error fetching financial details:', error);
        throw error;
    }
}
export async function createAccountFinancialDetails(organizationId, userId, input) {
    try {
        const { data, error } = await supabase
            .from('account_financial_details')
            .insert([
            {
                organization_id: organizationId,
                account_id: input.accountId,
                financial_status: input.financialStatus,
                next_payable_date: input.nextPayableDate,
                next_payment_usd: input.nextPaymentUSD,
                next_payment_inr: input.nextPaymentINR,
                payable_period_start: input.payablePeriodStart,
                payable_period_end: input.payablePeriodEnd,
                total_balance_usd: input.totalBalanceUSD,
                total_balance_inr: input.totalBalanceINR,
                usd_units: input.usdUnits,
                inr_units: input.inrUnits,
                created_by: userId,
            },
        ])
            .select()
            .single();
        if (error)
            throw error;
        return transformFinancialDetails(data);
    }
    catch (error) {
        console.error('Error creating financial details:', error);
        throw error;
    }
}
export async function updateAccountFinancialDetails(accountId, userId, updates) {
    try {
        const updateData = {};
        if (updates.financialStatus)
            updateData.financial_status = updates.financialStatus;
        if (updates.nextPayableDate)
            updateData.next_payable_date = updates.nextPayableDate;
        if (updates.nextPaymentUSD !== undefined)
            updateData.next_payment_usd = updates.nextPaymentUSD;
        if (updates.nextPaymentINR !== undefined)
            updateData.next_payment_inr = updates.nextPaymentINR;
        if (updates.payablePeriodStart)
            updateData.payable_period_start = updates.payablePeriodStart;
        if (updates.payablePeriodEnd)
            updateData.payable_period_end = updates.payablePeriodEnd;
        if (updates.totalBalanceUSD !== undefined)
            updateData.total_balance_usd = updates.totalBalanceUSD;
        if (updates.totalBalanceINR !== undefined)
            updateData.total_balance_inr = updates.totalBalanceINR;
        if (updates.usdUnits !== undefined)
            updateData.usd_units = updates.usdUnits;
        if (updates.inrUnits !== undefined)
            updateData.inr_units = updates.inrUnits;
        updateData.updated_at = new Date().toISOString();
        const { data, error } = await supabase
            .from('account_financial_details')
            .update(updateData)
            .eq('account_id', accountId)
            .select()
            .single();
        if (error)
            throw error;
        return transformFinancialDetails(data);
    }
    catch (error) {
        console.error('Error updating financial details:', error);
        throw error;
    }
}
// ============================================
// ACCOUNT BILLING SCHEDULE
// ============================================
export async function fetchAccountBillingSchedule(accountId) {
    try {
        const { data, error } = await supabase
            .from('account_billing_schedules')
            .select('*')
            .eq('account_id', accountId)
            .single();
        if (error && error.code !== 'PGRST116') {
            throw error;
        }
        return data ? transformBillingSchedule(data) : null;
    }
    catch (error) {
        console.error('Error fetching billing schedule:', error);
        throw error;
    }
}
export async function createAccountBillingSchedule(organizationId, userId, input) {
    try {
        const { data, error } = await supabase
            .from('account_billing_schedules')
            .insert([
            {
                organization_id: organizationId,
                account_id: input.accountId,
                billing_interval: input.billingInterval,
                priority: input.priority,
                urgency: input.urgency,
                monday_doc_reference: input.mondayDocReference,
                created_by: userId,
            },
        ])
            .select()
            .single();
        if (error)
            throw error;
        return transformBillingSchedule(data);
    }
    catch (error) {
        console.error('Error creating billing schedule:', error);
        throw error;
    }
}
export async function updateAccountBillingSchedule(accountId, updates) {
    try {
        const updateData = {};
        if (updates.billingInterval)
            updateData.billing_interval = updates.billingInterval;
        if (updates.priority)
            updateData.priority = updates.priority;
        if (updates.urgency)
            updateData.urgency = updates.urgency;
        if (updates.mondayDocReference)
            updateData.monday_doc_reference = updates.mondayDocReference;
        updateData.updated_at = new Date().toISOString();
        const { data, error } = await supabase
            .from('account_billing_schedules')
            .update(updateData)
            .eq('account_id', accountId)
            .select()
            .single();
        if (error)
            throw error;
        return transformBillingSchedule(data);
    }
    catch (error) {
        console.error('Error updating billing schedule:', error);
        throw error;
    }
}
// ============================================
// ACCOUNT CATEGORIZATION
// ============================================
export async function fetchAccountCategorization(accountId) {
    try {
        const { data, error } = await supabase
            .from('account_categorizations')
            .select('*')
            .eq('account_id', accountId)
            .single();
        if (error && error.code !== 'PGRST116') {
            throw error;
        }
        return data ? transformCategorization(data) : null;
    }
    catch (error) {
        console.error('Error fetching categorization:', error);
        throw error;
    }
}
export async function createAccountCategorization(organizationId, userId, input) {
    try {
        const { data, error } = await supabase
            .from('account_categorizations')
            .insert([
            {
                organization_id: organizationId,
                account_id: input.accountId,
                category: input.category,
                sub_category: input.subCategory,
                account_owner_group: input.accountOwnerGroup,
                account_type_category: input.accountTypeCategory,
                created_by: userId,
            },
        ])
            .select()
            .single();
        if (error)
            throw error;
        return transformCategorization(data);
    }
    catch (error) {
        console.error('Error creating categorization:', error);
        throw error;
    }
}
export async function updateAccountCategorization(accountId, updates) {
    try {
        const updateData = {};
        if (updates.category)
            updateData.category = updates.category;
        if (updates.subCategory)
            updateData.sub_category = updates.subCategory;
        if (updates.accountOwnerGroup)
            updateData.account_owner_group = updates.accountOwnerGroup;
        if (updates.accountTypeCategory)
            updateData.account_type_category = updates.accountTypeCategory;
        updateData.updated_at = new Date().toISOString();
        const { data, error } = await supabase
            .from('account_categorizations')
            .update(updateData)
            .eq('account_id', accountId)
            .select()
            .single();
        if (error)
            throw error;
        return transformCategorization(data);
    }
    catch (error) {
        console.error('Error updating categorization:', error);
        throw error;
    }
}
// ============================================
// TIME TRACKING
// ============================================
export async function fetchTimeTrackingEntries(organizationId, limit = 100) {
    try {
        const { data, error } = await supabase
            .from('time_tracking_entries')
            .select('*')
            .eq('organization_id', organizationId)
            .order('start_date', { ascending: false })
            .limit(limit);
        if (error)
            throw error;
        return (data || []).map(transformTimeTrackingEntry);
    }
    catch (error) {
        console.error('Error fetching time tracking entries:', error);
        throw error;
    }
}
export async function fetchTimeTrackingByAccount(accountId, limit = 50) {
    try {
        const { data, error } = await supabase
            .from('time_tracking_entries')
            .select('*')
            .eq('account_id', accountId)
            .order('start_date', { ascending: false })
            .limit(limit);
        if (error)
            throw error;
        return (data || []).map(transformTimeTrackingEntry);
    }
    catch (error) {
        console.error('Error fetching time tracking by account:', error);
        throw error;
    }
}
export async function createTimeTrackingEntry(organizationId, userId, input) {
    try {
        const { data, error } = await supabase
            .from('time_tracking_entries')
            .insert([
            {
                organization_id: organizationId,
                account_id: input.accountId,
                project_name: input.projectName,
                started_by: input.startedBy,
                ended_by: input.endedBy,
                start_date: input.startDate,
                end_date: input.endDate,
                start_time: input.startTime,
                end_time: input.endTime,
                duration: input.duration,
                notes: input.notes,
                created_by: userId,
            },
        ])
            .select()
            .single();
        if (error)
            throw error;
        return transformTimeTrackingEntry(data);
    }
    catch (error) {
        console.error('Error creating time tracking entry:', error);
        throw error;
    }
}
// ============================================
// ACCOUNT UPDATES HISTORY
// ============================================
export async function fetchAccountUpdates(accountId, limit = 50) {
    try {
        const { data, error } = await supabase
            .from('account_updates')
            .select('*')
            .eq('account_id', accountId)
            .order('created_at', { ascending: false })
            .limit(limit);
        if (error)
            throw error;
        return (data || []).map(transformAccountUpdate);
    }
    catch (error) {
        console.error('Error fetching account updates:', error);
        throw error;
    }
}
export async function createAccountUpdate(organizationId, accountId, userId, previousValues, newValues, changeReason) {
    try {
        const { data, error } = await supabase
            .from('account_updates')
            .insert([
            {
                organization_id: organizationId,
                account_id: accountId,
                previous_values: previousValues,
                new_values: newValues,
                changed_by: userId,
                change_reason: changeReason,
            },
        ])
            .select()
            .single();
        if (error)
            throw error;
        return transformAccountUpdate(data);
    }
    catch (error) {
        console.error('Error creating account update:', error);
        throw error;
    }
}
// ============================================
// ENHANCED ACCOUNT OPERATIONS
// ============================================
export async function fetchEnhancedAccount(accountId) {
    try {
        const account = await fetchAccountById(accountId);
        if (!account)
            return null;
        const [financialDetails, billingSchedule, categorization, lastUpdate] = await Promise.all([
            fetchAccountFinancialDetails(accountId),
            fetchAccountBillingSchedule(accountId),
            fetchAccountCategorization(accountId),
            fetchAccountUpdates(accountId, 1).then((updates) => updates[0] || undefined),
        ]);
        return {
            ...account,
            financialDetails: financialDetails || undefined,
            billingSchedule: billingSchedule || undefined,
            categorization: categorization || undefined,
            lastUpdated: lastUpdate,
        };
    }
    catch (error) {
        console.error('Error fetching enhanced account:', error);
        throw error;
    }
}
export async function fetchAllEnhancedAccounts(organizationId) {
    try {
        const accounts = await fetchAccounts(organizationId);
        const enhancedAccounts = await Promise.all(accounts.map(async (account) => {
            const enhanced = await fetchEnhancedAccount(account.id);
            return enhanced || account;
        }));
        return enhancedAccounts;
    }
    catch (error) {
        console.error('Error fetching all enhanced accounts:', error);
        throw error;
    }
}
// ============================================
// UTILITY FUNCTIONS
// ============================================
export function formatCurrency(value, currency = 'USD') {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
    }).format(value);
}
export function getAccountIcon(type) {
    const icons = {
        checking: '🏦',
        savings: '💰',
        cash: '💵',
        credit: '💳',
        investment: '📈',
        paypal: '🅿️',
        other: '💼',
    };
    return icons[type] || '💼';
}
export function getAccountTypeColor(type) {
    const colors = {
        checking: '#3B82F6',
        savings: '#10B981',
        cash: '#F59E0B',
        credit: '#EF4444',
        investment: '#8B5CF6',
        paypal: '#1E40AF',
        other: '#6B7280',
    };
    return colors[type] || '#6B7280';
}
